import React, { useState, useEffect } from 'react';
import { adminUsersAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import ConfirmModal from '../../components/common/ConfirmModal';
import Toast from '../../components/common/Toast';
import Avatar from '../../components/Avatar';

const AdminUserList = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, adminId: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'content_manager',
    is_active: true,
  });

  useEffect(() => {
    fetchAdmins();
  }, [currentPage, status, appliedSearch]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await adminUsersAPI.getAll({
        status,
        search: appliedSearch,
        page: currentPage,
        limit: 20,
      });
      if (response.data.success) {
        setAdmins(response.data.data.admins);
        setTotalPages(response.data.data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      showToast('Failed to fetch admin users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
    setCurrentPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAdmin) {
        await adminUsersAPI.update(editingAdmin.id, formData);
        showToast('Admin user updated successfully', 'success');
      } else {
        await adminUsersAPI.create(formData);
        showToast('Admin user created successfully', 'success');
      }
      resetForm();
      fetchAdmins();
    } catch (error) {
      showToast(error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      role: admin.role,
      is_active: admin.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (adminId) => {
    try {
      await adminUsersAPI.delete(adminId);
      showToast('Admin user deleted successfully', 'success');
      fetchAdmins();
    } catch (error) {
      showToast('Failed to delete admin user', 'error');
    }
    setConfirmModal({ show: false, adminId: null });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'content_manager',
      is_active: true,
    });
    setEditingAdmin(null);
    setShowModal(false);
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  return (
    <div>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <ConfirmModal
        show={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, adminId: null })}
        onConfirm={() => handleDelete(confirmModal.adminId)}
        title="Delete Admin User"
        message="Are you sure you want to delete this admin user? This action cannot be undone."
        type="danger"
      />

      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="page-title">Admin Users</h2>
            <p className="page-subtitle">Manage admin accounts and permissions</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="bi bi-plus-circle me-2"></i>
            Add Admin User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="stat-card mb-4">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Status Filter</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="active">Active Admins</option>
              <option value="inactive">Inactive Admins</option>
              <option value="deleted">Deleted Admins</option>
              <option value="all">All Admins</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Search</label>
            <form onSubmit={handleSearch}>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-primary" type="submit">
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </form>
          </div>
          <div className="col-md-2">
            <button
              className="btn btn-outline-primary w-100 text-center d-flex justify-content-center align-items-center"
              onClick={fetchAdmins}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="data-table">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Activity</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                      <p className="text-muted mb-0">No admin users found</p>
                    </td>
                  </tr>
                ) : (
                  admins.map((admin) => (
                    <tr key={admin.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Avatar name={admin.name} size={36} />
                          <div className="ms-2">
                            <div className="fw-semibold">{admin.name}</div>
                          </div>
                        </div>
                      </td>
                      <td>{admin.email}</td>
                      <td>
                        <span className={`badge ${
                          admin.role === 'super_admin' ? 'badge-danger' :
                          admin.role === 'content_manager' ? 'badge-purple' :
                          'badge-info'
                        }`}>
                          {admin.role.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        {admin.deleted_at ? (
                          <span className="badge badge-danger">Deleted</span>
                        ) : admin.is_active ? (
                          <span className="badge badge-success">Active</span>
                        ) : (
                          <span className="badge badge-danger">Inactive</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {admin.last_login_at 
                            ? new Date(admin.last_login_at).toLocaleString()
                            : 'Never'}
                        </small>
                      </td>
                      <td>
                        <small className="text-muted">{admin.activity_count || 0} actions</small>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEdit(admin)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => setConfirmModal({ show: true, adminId: admin.id })}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="p-3 border-top">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingAdmin ? 'Edit Admin User' : 'Add Admin User'}
                </h5>
                <button className="btn-close" onClick={resetForm}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Password {editingAdmin && '(leave blank to keep current)'}
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingAdmin}
                      minLength="8"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    >
                      <option value="super_admin">Super Admin</option>
                      <option value="content_manager">Content Manager</option>
                      <option value="support">Support</option>
                    </select>
                  </div>
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      Active
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingAdmin ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserList;